import Vue from 'vue';
import store from '../store';
import { API_BASE_URL } from './tenantHttp';
import { TENANCY_ENABLED, parseTenantResponse } from './tenant';

const state = Vue.observable({ links: {}, version: 0 });
const pending = {};
const refreshTimers = {};
let generation = 0;
const legacyPrefix = 'https://aws.msobcfdc.in/S3BucketApril/';

function documentRequest(url) {
  if (typeof url !== 'string' || url.indexOf(legacyPrefix) !== 0) {
    throw new Error('The document reference is not a supported portal document.');
  }
  const parts = url.slice(legacyPrefix.length).split('/');
  if (parts.length !== 2 || !parts[1] || parts[1] === '.' || parts[1] === '..' ||
      /[\\?#%]/.test(parts[1])) {
    throw new Error('The document filename is invalid.');
  }
  if (parts[0] === 'msobcfdc_bsc') return { SharedAssetName: parts[1] };
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(parts[0])) {
    throw new Error('The document owner is invalid.');
  }
  return { UserId: parts[0].toLowerCase(), FileName: parts[1] };
}

export function validateSignedDocument(body) {
  const result = parseTenantResponse(body);
  if (!result || typeof result.Url !== 'string') {
    throw new Error('The document API returned no signed URL.');
  }
  const target = new URL(result.Url);
  if (target.protocol !== 'https:' || target.username || target.password ||
      !/^[a-z0-9.-]+\.s3(?:[.-][a-z0-9-]+)?\.amazonaws\.com$/i.test(target.hostname)) {
    throw new Error('The document API returned an unsupported storage URL.');
  }
  const expires = Date.parse(result.ExpiresAt);
  if (!Number.isFinite(expires) || expires <= Date.now() + 1000) {
    throw new Error('The signed document URL is expired or has no expiry.');
  }
  return { url: result.Url, expires };
}

function reportDocumentError(error) {
  console.error('Tenant document access failed:', error);
  store.commit('SET_DOCUMENT_ERROR', 'Unable to access this document. ' +
    (error.message || 'Please contact support.'));
}

export function protectedDocumentUrl(legacyUrl) {
  if (!TENANCY_ENABLED) return legacyUrl;
  const scope = store.state.tenant && store.state.tenant.TenantId;
  const user = store.getters.loggedUser || {};
  const key = state.version + ':' + scope + ':' + (user.UserId || '') + ':' + legacyUrl;
  const cached = state.links[key];
  if (cached && cached.expires > Date.now() + 30000) return cached.url;
  if (pending[key]) return undefined;
  let request;
  try {
    if (!scope || !store.getters.authToken) {
      throw new Error('Sign in to access tenant documents.');
    }
    request = documentRequest(legacyUrl);
  } catch (error) {
    pending[key] = true;
    reportDocumentError(error);
    return undefined;
  }
  pending[key] = true;
  const requestedGeneration = generation;
  Vue.http.post(API_BASE_URL + 'Tenants/Documents/DownloadUrl', request, {
    headers: { Authorization: 'Bearer ' + store.getters.authToken }
  }).then(response => {
    if (requestedGeneration !== generation) return;
    const signed = validateSignedDocument(response.body);
    Vue.set(state.links, key, signed);
    refreshTimers[key] = setTimeout(() => {
      delete refreshTimers[key];
      Vue.delete(state.links, key);
    }, Math.max(0, signed.expires - Date.now() - 30000));
    delete pending[key];
  }).catch(error => {
    if (requestedGeneration === generation) reportDocumentError(error);
  });
  return undefined;
}

export function tenantUploadUrl(userId, fileName, contentType) {
  return Vue.http.post(API_BASE_URL + 'Tenants/Documents/UploadUrl', {
    UserId: userId, FileName: fileName, ContentType: contentType
  }, {
    headers: { Authorization: 'Bearer ' + store.getters.authToken }
  }).then(response => validateSignedDocument(response.body).url);
}

export function clearDocumentLinks() {
  generation++;
  Object.keys(refreshTimers).forEach(key => {
    clearTimeout(refreshTimers[key]);
    delete refreshTimers[key];
  });
  state.links = {};
  state.version++;
  Object.keys(pending).forEach(key => delete pending[key]);
}



// WEBPACK FOOTER //
// ./src/services/tenantDocuments.js